import { Box, Button, Flex, Group, NumberInput, SimpleGrid, Stack } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import dayjs from 'dayjs';
import { memo, useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBulkUpdateWorkPeriods } from '../../../api/work-periods-client/work-periods-client.hooks';
import type {
  BulkUpdateWorkPeriodsPayload,
  WorkPeriod,
} from '../../../api/work-periods-client/work-periods-client.type';
import { SidebarContext } from '../../../containers/side-bar/side-bar.context';
import { getCurrency } from '../../../helpers/currency-helpers';
import { formatDate, formatYearDate, safeParseDateInput } from '../../../helpers/date-time-helpers';
import { useFinancialsStore } from '../../../store/financials-store/financials-store';
import { useGlobalStore } from '../../../store/global-store/global-store';
import { newCOLORS } from '../../../styles/colors';
import { baseWhite } from '../../../styles/design-tokens';
import { Text } from '../../../ui-library/typography/typography';
import { useWorkPeriodsData } from '../../work-periods/hooks/work-periods.hooks';
import type { BudgetMap, SprintBudgetCardProps } from './by-sprint.type';

/**
 * Parses a Mantine NumberInput value into the numeric form stored in budget state.
 * Treats empty strings, null, undefined, and NaN as "no value" (undefined) so callers
 * can distinguish "user cleared the field" from "user typed a zero".
 *
 * @param {number | string | null | undefined} value - Raw value emitted by NumberInput's onChange
 * @returns {number | undefined} A finite number, or undefined when the input is blank/invalid
 */
function parseBudget(value: number | string | null | undefined): number | undefined {
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  if (value === '' || value === null || value === undefined) return undefined;
  // Strip thousand-separator commas before parsing. Mantine's NumberInput usually
  // normalizes these before emitting, but `decimalScale` + `thousandSeparator=","`
  // can route the raw formatted string through onChange in some edge cases.
  const parsed = Number.parseFloat(value.replace(/,/g, ''));
  return Number.isFinite(parsed) ? parsed : undefined;
}

/**
 * Pre-computes a numeric timestamp for sort comparison so the sort comparator is O(1)
 * per call instead of invoking `dayjs(...)` per comparison (which was 2× O(n log n)
 * in the previous implementation). The returned array is frozen so any future
 * `.sort()` mutation regresses loudly via `TypeError` in strict mode.
 *
 * @param {WorkPeriod[]} workPeriods - All work periods for the selected board
 * @param {number | null} selectedYear - The active year filter; when null, returns []
 * @returns {readonly WorkPeriod[]} Frozen, year-filtered, chronologically-sorted work periods
 */
function buildSortedFilteredWorkPeriods(
  workPeriods: WorkPeriod[] | undefined,
  selectedYear: number | null,
): readonly WorkPeriod[] {
  if (!selectedYear || !workPeriods) return Object.freeze([]);

  const now = dayjs();
  const yearString = selectedYear.toString();
  const filtered: { wp: WorkPeriod; ts: number }[] = [];
  for (const workPeriod of workPeriods) {
    if (formatYearDate(workPeriod.end_date) !== yearString) continue;
    if (safeParseDateInput(workPeriod.end_date)?.isBefore(now) !== true) continue;
    filtered.push({ wp: workPeriod, ts: dayjs(workPeriod.start_date).valueOf() });
  }
  filtered.sort((a, b) => a.ts - b.ts);
  return Object.freeze(filtered.map((entry) => entry.wp));
}

/**
 * Builds a stable, content-aware fingerprint of the persisted budgets in `workPeriods`.
 * Used as the dependency of the reset effect so that React Query background refetches
 * returning identical data do not re-initialize local state.
 *
 * @param {readonly WorkPeriod[]} workPeriods - The sorted, filtered work periods
 * @returns {string} A delimited string keying each work period to its persisted budget
 */
function computeBudgetFingerprint(workPeriods: readonly WorkPeriod[]): string {
  return workPeriods.map((wp) => `${wp.id}:${wp.budget ?? ''}`).join('|');
}

/**
 * "Edit Financial Data → By Sprint" tab. Renders a grid of per-sprint budget inputs.
 * Persists on Save Changes; no per-keystroke API calls.
 *
 * @returns {JSX.Element} The By Sprint editor
 */
function BySprint() {
  const { t } = useTranslation();
  const { viewAreaWidth } = useContext(SidebarContext);
  const selectedYear = useFinancialsStore((state) => state.table.selectedYear);
  const selectedBoard = useFinancialsStore((state) => state.board);
  const { data: workPeriods } = useWorkPeriodsData(selectedBoard);
  const organization = useGlobalStore((state) => state.organization);
  const { code: currencyCode, symbol: currencySymbol } = getCurrency(organization);

  const [budgetValues, setBudgetValues] = useState<BudgetMap>({});
  const [originalBudgetValues, setOriginalBudgetValues] = useState<BudgetMap>({});
  // Mirror of `originalBudgetValues` accessible inside the stable `handleBudgetChange`
  // closure without forcing a `useCallback` dependency. Without this ref, every save
  // (which calls `setOriginalBudgetValues`) would flip the callback's identity and
  // bust `React.memo` on every `SprintBudgetCard`. The ref is synced via the effect
  // below so reads always see the latest snapshot. See https://react.dev/reference/react/useRef.
  const originalBudgetValuesRef = useRef(originalBudgetValues);
  useEffect(() => {
    originalBudgetValuesRef.current = originalBudgetValues;
  }, [originalBudgetValues]);
  const [dirtyCount, dispatchDirty] = useReducer((count: number, delta: number) => Math.max(0, count + delta), 0);
  const hasChanges = dirtyCount > 0;

  const filteredWorkPeriods = useMemo(
    () => buildSortedFilteredWorkPeriods(workPeriods, selectedYear),
    [workPeriods, selectedYear],
  );

  const budgetFingerprint = useMemo(() => computeBudgetFingerprint(filteredWorkPeriods), [filteredWorkPeriods]);

  // Pending refs hold a refetch deferred while the user is editing. Without
  // these, a snapshot that arrives during typing is dropped permanently (the
  // effect bails once and `budgetFingerprint` never changes back). The
  // companion `useEffect` watching `dirtyCount` drains the queue once the form
  // becomes clean — either via Save (where we clear pending first because the
  // post-save invalidation provides a fresher merged snapshot) or via the user
  // typing back to the original value.
  const pendingBudgetFingerprintRef = useRef<string | null>(null);
  const pendingInitialValuesRef = useRef<BudgetMap | null>(null);

  // `budgetFingerprint` is a content-aware string derived from `filteredWorkPeriods`.
  // We use it as the sole dep so that React Query background refetches returning the
  // same content (different array ref, same fingerprint) do NOT trigger a reset.
  // `dirtyCount` is read for an early bail-out only — including it as a dep would run
  // the effect on every keystroke. `filteredWorkPeriods` is read inside the body but
  // its content is captured by the fingerprint dep.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see comment above
  useEffect(() => {
    const initialValues: BudgetMap = {};
    for (const workPeriod of filteredWorkPeriods) {
      initialValues[workPeriod.id] = workPeriod.budget ?? undefined;
    }
    if (dirtyCount > 0) {
      pendingBudgetFingerprintRef.current = budgetFingerprint;
      pendingInitialValuesRef.current = initialValues;
      return;
    }
    setBudgetValues(initialValues);
    setOriginalBudgetValues(initialValues);
  }, [budgetFingerprint]);

  // Drain the queued snapshot when dirty count returns to 0. This covers both
  // post-Save (where `handleSave` clears pending first, so this is a no-op) and
  // the rarer case where the user types back to the original value during a
  // session that received a concurrent refetch.
  useEffect(() => {
    if (dirtyCount !== 0) return;
    const pendingFp = pendingBudgetFingerprintRef.current;
    const pendingValues = pendingInitialValuesRef.current;
    if (!pendingFp || !pendingValues) return;
    pendingBudgetFingerprintRef.current = null;
    pendingInitialValuesRef.current = null;
    setBudgetValues(pendingValues);
    setOriginalBudgetValues(pendingValues);
  }, [dirtyCount]);

  const bulkUpdateMutation = useBulkUpdateWorkPeriods({
    subprojectId: selectedBoard || '',
  });

  const handleBudgetChange = useCallback((workPeriodId: string, value: number | string) => {
    const numValue = parseBudget(value);
    setBudgetValues((prev) => {
      if (prev[workPeriodId] === numValue) return prev;
      const original = originalBudgetValuesRef.current[workPeriodId];
      const wasDirtyForId = prev[workPeriodId] !== original;
      const isDirtyForId = numValue !== original;
      if (wasDirtyForId !== isDirtyForId) {
        dispatchDirty(isDirtyForId ? 1 : -1);
      }
      return { ...prev, [workPeriodId]: numValue };
    });
  }, []);

  const handleSave = async () => {
    if (!selectedBoard) {
      notifications.show({
        title: 'Error',
        message: 'Please select a board first',
        color: 'red',
      });
      return;
    }

    const payload: BulkUpdateWorkPeriodsPayload[] = filteredWorkPeriods
      .map((workPeriod) => ({
        id: workPeriod.id,
        budget: budgetValues[workPeriod.id] ?? 0,
      }))
      .filter((item) => budgetValues[item.id] !== originalBudgetValues[item.id]);

    if (payload.length === 0) {
      notifications.show({
        title: 'No Changes',
        message: 'No budget values have been modified',
        color: 'orange',
      });
      return;
    }

    try {
      await bulkUpdateMutation.mutateAsync(payload);
      // Clear any refetch deferred during typing. The post-save invalidation
      // refetch will deliver the merged server snapshot; applying the queued
      // pre-save copy would only flicker before being overwritten.
      pendingBudgetFingerprintRef.current = null;
      pendingInitialValuesRef.current = null;
      notifications.show({
        title: 'Success',
        message: `Updated ${payload.length} work period budget${payload.length === 1 ? '' : 's'} successfully`,
        color: 'green',
      });
      setOriginalBudgetValues({ ...budgetValues });
      // Reset the dirty counter by dispatching the negation of the captured count.
      // The reducer clamps at 0, so this composes safely with any in-flight keystroke
      // increments that landed between `mutateAsync` resolution and this dispatch:
      // the resulting count reflects the net dirty cells edited AFTER the save round-trip.
      dispatchDirty(-dirtyCount);
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to update budget values',
        color: 'red',
      });
    }
  };

  return (
    <Box
      style={{
        padding: '16px 24px',
        marginBottom: '16px',
        backgroundColor: baseWhite,
        boxShadow: '0 2px 3px rgba(0, 0, 0, 0.1)',
        maxWidth: viewAreaWidth - 80,
        overflow: 'hidden',
      }}
    >
      <Stack gap={16}>
        <Flex justify="space-between">
          <Group align="center" justify="flex-start">
            <Text size="large">Development spend</Text>
          </Group>
          <Group align="center">
            <Text>{`in ${currencySymbol} ${currencyCode}`}</Text>
            {hasChanges && (
              <Button onClick={handleSave} color={newCOLORS.indigo} loading={bulkUpdateMutation.isPending} size="sm">
                Save Changes
              </Button>
            )}
          </Group>
        </Flex>
        {filteredWorkPeriods.length ? (
          <SimpleGrid cols={{ base: 2, xs: 2, sm: 4, md: 5, lg: 6, xl: 7 }} spacing="sm" style={{ width: '100%' }}>
            {filteredWorkPeriods.map((workPeriod) => (
              <SprintBudgetCard
                key={workPeriod.id}
                workPeriod={workPeriod}
                value={budgetValues[workPeriod.id]}
                disabled={dayjs(workPeriod.start_date).isAfter(dayjs())}
                onChange={handleBudgetChange}
                placeholder={t('financials.placeholders.enter_budget')}
              />
            ))}
          </SimpleGrid>
        ) : (
          <Text>
            {(workPeriods || []).length === 0
              ? t('financials.table.no_work_periods_found')
              : t('financials.table.no_work_periods_for_year', { year: selectedYear || 'selected year' })}
          </Text>
        )}
      </Stack>
    </Box>
  );
}

/**
 * Single sprint card with a labeled NumberInput for the per-sprint budget.
 * Memoized: typing into one card no longer re-renders its siblings because the
 * parent passes a stable `onChange` (useCallback) and primitive props.
 *
 * @param {SprintBudgetCardProps} props - Card props
 * @returns {JSX.Element} The rendered card
 */
const SprintBudgetCard = memo(function SprintBudgetCard({
  workPeriod,
  value,
  disabled,
  onChange,
  placeholder,
}: SprintBudgetCardProps) {
  const handleChange = useCallback((next: number | string) => onChange(workPeriod.id, next), [onChange, workPeriod.id]);

  return (
    <Stack gap={0} align="center" justify="space-between" style={{ width: '100%', minHeight: '80px' }}>
      <Box
        style={{
          flex: '1 1 auto',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: '2px',
        }}
      >
        <Text weight="bold" style={{ textAlign: 'center', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
          {workPeriod.name}
        </Text>
        <Text
          size="tiny"
          style={{ textAlign: 'center', wordBreak: 'break-word', fontSize: '0.75rem' }}
        >{`${formatDate(workPeriod.start_date)} - ${formatDate(workPeriod.end_date)}`}</Text>
      </Box>
      <NumberInput
        hideControls
        style={{ width: '100%', maxWidth: '200px' }}
        radius="md"
        disabled={disabled}
        value={value ?? ''}
        onChange={handleChange}
        placeholder={placeholder}
        min={0}
        decimalScale={2}
        fixedDecimalScale={false}
        thousandSeparator=","
      />
    </Stack>
  );
});

export { BySprint, buildSortedFilteredWorkPeriods, computeBudgetFingerprint, parseBudget, SprintBudgetCard };
